import {Button, Modal, Select} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Divider} from 'src/design/atoms/Divider';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {MIME_TYPES} from '@mantine/dropzone';
import {IconCloudDownload, IconInfoCircle} from '@tabler/icons-react';
import React, {useState} from 'react';
import type {IFormDocumentUploadFile} from 'src/app/forms/FormFileDropzone/FormFileUploadV2';
import {FormFileUploadV2} from 'src/app/forms/FormFileDropzone/FormFileUploadV2';
import {useGetSiteSelectOptionsQuery} from 'src/redux/queries/site';
import {ApiEndpoints} from 'src/shared/constants/apiEndpoints';
import {useAlert} from 'src/shared/hooks/useAlert';
import ApiCaller, {ApiCallerDownloadFile} from 'src/shared/utils/ApiCaller';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {isValidArray} from 'src/utilities/objectUtils';
import type {IFormFieldV2} from 'src/types/FormV2';

const fieldData: IFormFieldV2 = {
  name: 'deviceImport',
  label: 'Device File Upload',
  type: 'file',
  description:
    'Use the file upload below to select a .CSV file containing your device data. The data file must match the import template specification. You may download a copy of the template to match with your data using the "Download Template" button below.',
  fileUploadOptions: {
    allowedFileTypes: [MIME_TYPES.csv],
    isHideUploadSuccessEnabled: true,
  },
};

export interface ImportDevicesModalProps {
  opened: boolean;
  onClose: () => void;
  /** Preselects (and hides) the target site when embedded on `SiteView`. */
  siteId?: number;
}

export const ImportDevicesModal: React.FC<ImportDevicesModalProps> = ({
  opened,
  onClose,
  siteId,
}) => {
  const {setAlert} = useAlert();
  const [isSaving, setIsSaving] = useState(false);
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(
    siteId ? String(siteId) : null
  );
  const [importedFiles, setImportedFiles] = useState<IFormDocumentUploadFile[]>(
    []
  );

  const {data: sites, isLoading: isSitesLoading} = useGetSiteSelectOptionsQuery(
    {
      start: 0,
      length: 10000,
      filters: [],
      filterFns: [],
      sorting: [],
    },
    {skip: siteId !== undefined}
  );

  const handleUpload = (
    _name: string,
    _value: string,
    uploadedFiles: IFormDocumentUploadFile[]
  ) => {
    setImportedFiles(uploadedFiles);
  };

  const targetSiteId =
    siteId ?? (selectedSiteId ? Number(selectedSiteId) : undefined);

  const handleConfirmImport = async () => {
    if (!isValidArray(importedFiles) || !targetSiteId) return;

    setIsSaving(true);

    try {
      await ApiCaller({
        method: 'POST',
        endpointURL: ApiEndpoints.deviceImport,
        data: {
          key: importedFiles[0].encodedKey,
          siteId: targetSiteId,
        },
      });
      setAlert(
        'Import started. Results will be emailed to you when processing finishes.',
        'success'
      );
      handleClose();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }

    setIsSaving(false);
  };

  const handleDownloadImportTemplate = () => {
    ApiCallerDownloadFile(
      ApiEndpoints.deviceImportTemplate,
      'device_import_template.csv'
    ).catch((error: unknown) => setAlert(parseErrorMessage(error), 'error'));
  };

  const handleClose = () => {
    setImportedFiles([]);
    setSelectedSiteId(siteId ? String(siteId) : null);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Import Devices"
      size="lg">
      <Stack gap="md">
        <Alert icon={<IconInfoCircle />} color="blue" title="Before you import">
          Devices are matched by serial number: existing devices are updated in
          place and new ones are added. Devices already on file that are missing
          from your import are never removed. Files may contain up to 10,000
          rows, and results are emailed to you once processing finishes.
        </Alert>
        <Divider />
        {siteId === undefined && (
          <Select
            label="Site"
            placeholder="Select the site these devices belong to"
            required
            searchable
            disabled={isSitesLoading}
            data={(sites?.data ?? []).map(option => ({
              value: String(option.id),
              label: option.label ?? String(option.id),
            }))}
            value={selectedSiteId}
            onChange={setSelectedSiteId}
          />
        )}
        <FormFileUploadV2
          field={fieldData}
          value={importedFiles}
          onChange={handleUpload}
        />
        <Group justify="space-between">
          <Button
            color="dark"
            variant="subtle"
            onClick={handleDownloadImportTemplate}
            leftSection={<IconCloudDownload size={16} />}>
            Download Template
          </Button>
          <Group>
            <Button color="dark" variant="subtle" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmImport}
              disabled={!isValidArray(importedFiles) || !targetSiteId}
              loading={isSaving}>
              Import
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
};
